import { useState } from 'react'
import { Button, SavedStatus, FullscreenModal, CaseDetails } from 'components'
import Icon from 'components/priority/Icon/Icon'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import * as S from './EditorHeader.styled'
import zLayers from 'utils/styling/zLayers'
import { EventCategory, logger } from 'utils/logger'
import { useRecoilLogData } from 'utils/recoilLogger'
import LanguageSelector from 'components/global/LanguageSelector/LanguageSelector'
import { useTranslation } from 'react-i18next'
import { roleAtom } from '@/atoms'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { applicationTypeAtom } from '@/atoms/globalAtoms'
import { useRecoilValue, useRecoilState } from 'recoil'
import {
  hasAnyImageQualityIssuesSelector,
  imageQualityThresholdSelector,
  showImageQualityModalAtom,
  showImageQualityAcknowledgeButtonAtom,
  initialImageQualityCheckCompletedAtom,
} from '@/atoms/imageQualityAtoms'
import {
  hasBleedWarningsSelector,
  showBleedWarningModalAtom,
  bleedCheckCompletedAtom,
} from '@/atoms/bleedWarningAtoms'
import { orderAtom, currentProductIdAtom } from '@/atoms/printPackageAtoms'
import {
  DESIGNER_SPACE_LABELS,
  EDIT_TOKEN_ROLE,
  EDIT_TOKEN_STORAGE_KEYS,
  M3,
} from '@messenger/consts'
import { SendProofModal } from './SendProofModal'
import { unsavedChangesAtom } from 'atoms'
import { isDesignerTokenSession } from 'utils/global/designerSession'
import { PrintImageQualityModal } from '@/components/page/PrintImageQualityModal/PrintImageQualityModal'
import { BleedWarningModal } from '@/components/editor/BleedWarningModal/BleedWarningModal'
import { useBackBleedCheckWait } from '@/hooks/editor/useBackBleedCheckWait'
import { notifyEditorBackButton } from '@/api/orders'
import {
  isBulkProcessingAtom,
  isSavingAtom,
  isEditorReadyAtom,
} from '@/atoms/_editorAtoms'

export const EditorHeader = ({ back, update, assetDetails, ...props }) => {
  countEditorProbeRender('EditorHeader')
  const { t } = useTranslation()
  const recoilLogData = useRecoilLogData()
  const userRole = useRecoilValue(roleAtom)
  const isBulkProcessing = useRecoilValue(isBulkProcessingAtom)
  const isSaving = useRecoilValue(isSavingAtom)
  const isEditorReady = useRecoilValue(isEditorReadyAtom)
  const [showCaseDetailsModal, setShowCaseDetailsModal] = useState(false)

  // M3 PDF integration atoms
  const order = useRecoilValue(orderAtom)
  const currentProductId = useRecoilValue(currentProductIdAtom)
  const applicationType = useRecoilValue(applicationTypeAtom)

  // Designer token sessions can send the saved design to the client as a
  // proof; Send stays disabled until changes are saved so the proof always
  // matches what the designer sees. The grant only applies to the product
  // the token was scoped to — a stale grant from an earlier tab session
  // never surfaces the button on other work.
  const unsavedChanges = useRecoilValue(unsavedChangesAtom)
  const [showSendProofModal, setShowSendProofModal] = useState(false)
  const editTokenRole = sessionStorage.getItem(EDIT_TOKEN_STORAGE_KEYS.role)
  const designerToken = sessionStorage.getItem(EDIT_TOKEN_STORAGE_KEYS.token)
  const designerScope = sessionStorage.getItem(
    EDIT_TOKEN_STORAGE_KEYS.scopeOrderProductId,
  )
  const isDesignerSession =
    editTokenRole === EDIT_TOKEN_ROLE.DESIGNER &&
    Boolean(designerToken) &&
    Boolean(designerScope) &&
    designerScope === currentProductId

  const hasAnyImageQualityIssues = useRecoilValue(
    hasAnyImageQualityIssuesSelector,
  )
  const imageQualityThreshold = useRecoilValue(imageQualityThresholdSelector)
  const [showImageQualityModal, setShowImageQualityModal] = useRecoilState(
    showImageQualityModalAtom,
  )
  const [
    showImageQualityAcknowledgeButton,
    setShowImageQualityAcknowledgeButton,
  ] = useRecoilState(showImageQualityAcknowledgeButtonAtom)
  const initialImageQualityCheckCompleted = useRecoilValue(
    initialImageQualityCheckCompletedAtom,
  )
  const imageQualityCheckInProgress = !initialImageQualityCheckCompleted

  const hasBleedWarnings = useRecoilValue(hasBleedWarningsSelector)
  const bleedCheckCompleted = useRecoilValue(bleedCheckCompletedAtom)
  const bleedCheckInProgress = !bleedCheckCompleted
  const [showBleedWarningModal, setShowBleedWarningModal] = useRecoilState(
    showBleedWarningModalAtom,
  )

  const handleSave = () => {
    update()
    logger.info({
      message: 'editor_save_changes_button_clicked',
      category: EventCategory.CLICK,
      data: {
        ...recoilLogData,
        threshold: imageQualityThreshold,
      },
    })
  }

  const closeImageQualityModal = () => {
    setShowImageQualityModal(false)
    setShowImageQualityAcknowledgeButton(false)
  }

  const notifyM3EditorBackButton = async () => {
    // Only call for M3 applications
    if (applicationType === M3 && order?.id && currentProductId) {
      try {
        const payload = {
          orderId: order.id,
          orderProductId: currentProductId,
          applicationType: M3,
        }

        await notifyEditorBackButton(payload)
      } catch (error) {
        logger.error('Error notifying M3:', error)
      }
    }
  }

  const acknowledgeImageQualityModal = async () => {
    setShowImageQualityModal(false)
    setShowImageQualityAcknowledgeButton(false)

    // Notify M3 backend about editor back button click
    await notifyM3EditorBackButton()

    back()
    logger.info({
      message: 'editor_back_button_clicked',
      category: EventCategory.CLICK,
      data: {
        ...recoilLogData,
      },
    })
  }

  const proceedAfterBleedWarning = () => {
    if (imageQualityCheckInProgress) {
      setShowImageQualityModal(true)
      setShowImageQualityAcknowledgeButton(false)
      logger.info({
        message: 'editor_back_blocked_quality_check_in_progress',
        category: EventCategory.CLICK,
        data: {
          ...recoilLogData,
          gatingReason: 'image_quality_check_in_progress',
          threshold: imageQualityThreshold,
        },
      })
      return
    }

    if (hasAnyImageQualityIssues) {
      setShowImageQualityModal(true)
      setShowImageQualityAcknowledgeButton(true)
      logger.info({
        message: 'editor_back_blocked_quality_issues',
        category: EventCategory.CLICK,
        data: {
          ...recoilLogData,
          gatingReason: 'image_quality_issues_detected',
          threshold: imageQualityThreshold,
        },
      })
      return
    }

    // Notify M3 backend about editor back button click
    notifyM3EditorBackButton().then(() => {
      back()
      logger.info({
        message: 'editor_back_button_clicked',
        category: EventCategory.CLICK,
        data: {
          ...recoilLogData,
        },
      })
    })
  }

  const handleBleedWarningContinue = () => {
    setShowBleedWarningModal(false)
    logger.info({
      message: 'editor_back_with_bleed_warnings',
      category: EventCategory.CLICK,
      data: {
        ...recoilLogData,
      },
    })
    proceedAfterBleedWarning()
  }

  const handleBleedWarningGoBack = () => {
    setShowBleedWarningModal(false)
    logger.info({
      message: 'editor_bleed_warning_go_back',
      category: EventCategory.CLICK,
      data: {
        ...recoilLogData,
      },
    })
    // User stays in editor to fix issues
  }

  const openBleedWarningModal = () => {
    setShowBleedWarningModal(true)
    logger.info({
      message: 'editor_back_blocked_bleed_warnings',
      category: EventCategory.CLICK,
      data: {
        ...recoilLogData,
        gatingReason: 'bleed_warnings_detected',
      },
    })
  }

  const handleBleedCheckWaitResolved = (outcome, waitedMs) => {
    if (outcome === 'timedOut') {
      logger.warn({
        message: 'editor_back_bleed_check_timed_out',
        category: EventCategory.PERFORMANCE,
        data: {
          ...recoilLogData,
          waitedMs,
        },
      })
      proceedAfterBleedWarning()
      return
    }

    logger.info({
      message: 'editor_back_waited_for_bleed_check',
      category: EventCategory.PERFORMANCE,
      data: {
        ...recoilLogData,
        waitedMs,
        outcome,
      },
    })
    if (outcome === 'bleeding') {
      openBleedWarningModal()
      return
    }
    proceedAfterBleedWarning()
  }

  const bleedCheckWait = useBackBleedCheckWait({
    onResolved: handleBleedCheckWaitResolved,
  })

  const handleBleedCheckWaitClose = () => {
    bleedCheckWait.cancel()
    logger.info({
      message: 'editor_bleed_check_wait_closed',
      category: EventCategory.CLICK,
      data: {
        ...recoilLogData,
      },
    })
    // User stays in editor
  }

  const handleBack = () => {
    // A check still running has no verdict yet: Back waits for it in the
    // margin modal's checking state instead of leaving unchecked.
    if (bleedCheckInProgress) {
      bleedCheckWait.start()
      logger.info({
        message: 'editor_back_blocked_bleed_check_in_progress',
        category: EventCategory.CLICK,
        data: {
          ...recoilLogData,
          gatingReason: 'bleed_check_in_progress',
        },
      })
      return
    }

    if (hasBleedWarnings) {
      openBleedWarningModal()
      return
    }

    proceedAfterBleedWarning()
  }

  return (
    <>
      <Flex
        blur
        full
        background="lockedAlmostBlack"
        bgOpacity="80"
        height="70px"
        position="fixed"
        zindex={zLayers.contentBase}
      />
      <S.Navigation
        full
        justify="space-between"
        align="center"
        height="70px"
        padding="0 36px"
        zindex={zLayers.contentBase}
        position="fixed"
      >
        <Flex align="center">
          <Button
            data-testid="editor-back-button"
            outline
            iconLeft="chevronLeft"
            onClick={() => handleBack()}
            disabled={isBulkProcessing || isSaving}
          >
            {t('editor.backButton')}
          </Button>
          <SavedStatus />
        </Flex>
        <Flex column gap="2px" align="center">
          <Flex>
            <Text color="lockedWhite" margin="0 8px 0 0">
              {assetDetails && assetDetails}
            </Text>
            <Icon icon="info" fill="gray3" />
          </Flex>
          <Text color="gray" margin="0 16px 0 0" size="12px">
            {t('user.accessLabel')}{' '}
            {isDesignerTokenSession()
              ? t('user.roles.designer')
              : userRole === 'messenger-user'
                ? t('user.roles.superAdmin')
                : t('user.roles.funeralHome')}
          </Text>
        </Flex>
        <Flex align="center">
          {isDesignerSession && (
            <Button
              primary
              margin="0 8px 0 0"
              onClick={() => setShowSendProofModal(true)}
              disabled={
                isBulkProcessing || isSaving || !isEditorReady || unsavedChanges
              }
              data-testid="editor-send-proof-button"
            >
              {DESIGNER_SPACE_LABELS.sendProof.button}
            </Button>
          )}
          <Button
            primary
            onClick={() => handleSave()}
            disabled={isBulkProcessing || isSaving || !isEditorReady}
            data-testid="editor-save-button"
          >
            {t('editor.saveButton')}
          </Button>
          {applicationType !== M3 && <LanguageSelector whiteIcon />}
        </Flex>
      </S.Navigation>
      <Flex full height="70px" background="lockedAlmostBlack" {...props} />

      <FullscreenModal show={[showCaseDetailsModal, setShowCaseDetailsModal]}>
        <CaseDetails />
      </FullscreenModal>

      {isDesignerSession && (
        <SendProofModal
          show={[showSendProofModal, setShowSendProofModal]}
          token={designerToken}
        />
      )}

      <PrintImageQualityModal
        show={showImageQualityModal}
        onClose={closeImageQualityModal}
        onAcknowledge={acknowledgeImageQualityModal}
        threshold={imageQualityThreshold}
        showAcknowledgeButton={showImageQualityAcknowledgeButton}
      />

      <BleedWarningModal
        show={showBleedWarningModal || bleedCheckWait.waiting}
        checking={bleedCheckWait.waiting}
        onClose={
          bleedCheckWait.waiting
            ? handleBleedCheckWaitClose
            : handleBleedWarningGoBack
        }
        onContinue={handleBleedWarningContinue}
      />
    </>
  )
}

EditorHeader.displayName = 'EditorHeader'

export default EditorHeader
